import { useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { format } from 'date-fns';
import {
	makeGetCalculateReimbursementPreview,
	makePatchFinishRequestPreview,
	makePostFinishRequestPreview,
	makePostPreviewsAnalysis,
	makePostPreviewsProcedure,
} from 'main/factories/usecases/private/refund/request-preview';
import type {
	GetCalculateReimbursementPreviewParams,
	GetCalculateReimbursementPreviewResponse,
	PostFinishRequestPreviewParams,
	PostFinishRequestPreviewResponse,
	PostPreviewsAnalysisParams,
	PostPreviewsProcedureParams,
} from 'domain/usecases/private/refund/request-preview';
import { PreviewRequestFormSchemaValues } from '../schema';
import { SelectTypeOptionCodes, SelectTypeOptionLabels } from '../../SelectTypeV2/types';
import { usePersonalData } from 'presentation/hooks';
import { useRequestPreviewFlowStore } from '../../../store/useRequestPreviewFlowStore';

export const useFinishPreview = (handleNextStep?: () => void) => {
	const stepCompletedRef = useRef(0);
	const [isValueZero, setIsValueZero] = useState(false);
	const lastParamsRef = useRef<PreviewRequestFormSchemaValues | null>(null);
	const lastCalculateResultRef = useRef<GetCalculateReimbursementPreviewResponse | null>(null);
	const lastFinishResultRef = useRef<PostFinishRequestPreviewResponse | null>(null);
	const { currentEmail, currentPhone } = usePersonalData();
	const { selectedServiceTypeOption, setFinishPreviewParams, setPreviewsAnalysisResult } = useRequestPreviewFlowStore(state => state);
   const consultationProcedureCode = '10101012';

	const calculatePreviewMutation = useMutation({
		mutationFn: (params: GetCalculateReimbursementPreviewParams) =>
			makeGetCalculateReimbursementPreview().get(params),
	});

	const finishPreviewMutation = useMutation({
		mutationFn: (params: PostFinishRequestPreviewParams) =>
			makePostFinishRequestPreview().post(params),
	});

	const previewsProcedureMutation = useMutation({
		mutationFn: (params: PostPreviewsProcedureParams) =>
			makePostPreviewsProcedure().post(params),
	});

	const patchFinishRequestPreviewMutation = useMutation({
		mutationFn: (protocol: string) =>
			makePatchFinishRequestPreview(protocol).patch(),
	});

	const postPreviewsAnalysisMutation = useMutation({
		mutationFn: (params: PostPreviewsAnalysisParams) =>
			makePostPreviewsAnalysis().post(params),
	});

	const isLoading =
		calculatePreviewMutation.isPending ||
		finishPreviewMutation.isPending ||
		previewsProcedureMutation.isPending ||
		patchFinishRequestPreviewMutation.isPending ||
		postPreviewsAnalysisMutation.isPending;

	const hasError =
		!!calculatePreviewMutation.error ||
		!!finishPreviewMutation.error ||
		!!previewsProcedureMutation.error ||
		!!patchFinishRequestPreviewMutation.error ||
		!!postPreviewsAnalysisMutation.error;


	const handleFinishPreview = async (params?: PreviewRequestFormSchemaValues) => {
		const previousParams = lastParamsRef.current;
		const safeParams = params ?? previousParams;
		if (!safeParams) return;

		const procedureCode =
			selectedServiceTypeOption === SelectTypeOptionLabels.CONSULTA
				? consultationProcedureCode
				: (safeParams.selectedProcedure?.code ?? '');
		const patientCode = safeParams.beneficiary?.userCode ?? '';
		const calculateUserCode = patientCode;

		const previousProcedureCode =
			previousParams && selectedServiceTypeOption === SelectTypeOptionLabels.CONSULTA
				? consultationProcedureCode
				: previousParams?.selectedProcedure?.code ?? '';
		const previousPatientCode = previousParams?.beneficiary?.userCode ?? '';
		const procedureOrPatientChanged =
			procedureCode !== previousProcedureCode || patientCode !== previousPatientCode;
		if (previousParams != null && stepCompletedRef.current > 0 && procedureOrPatientChanged) {
			resetFlowFromStart();
		}

		lastParamsRef.current = safeParams;
		setFinishPreviewParams(safeParams);
		const modality = selectedServiceTypeOption?.toUpperCase() ?? '';
		const modalityCode = SelectTypeOptionCodes[modality];

		if (stepCompletedRef.current < 1) {
			const calculateResult = await calculatePreviewMutation.mutateAsync({
				userCode: calculateUserCode,
				partnerCompany: String(patientCode).slice(0, 5),
				procedureCode,
				typeService: 'P',
				procedureServiceQuantity: '1',
			});

			if (calculateResult.content.value <= 0) {
				setIsValueZero(true);
				return;
			}

			lastCalculateResultRef.current = calculateResult;
			stepCompletedRef.current = 1;
		}

		if (stepCompletedRef.current < 2) {
			const finishResult = await finishPreviewMutation.mutateAsync({
				email: currentEmail ?? '',
				phoneNumber: currentPhone ?? '',
				patientCode,
				userCode: calculateUserCode,
				modality,
				modalityCode,
			});

			lastFinishResultRef.current = finishResult;
			stepCompletedRef.current = 2;
		}

		if (stepCompletedRef.current < 3) {
			const calculateData = lastCalculateResultRef.current ?? calculatePreviewMutation.data;
			const finishData = lastFinishResultRef.current?.content ?? finishPreviewMutation.data?.content;
			const value = String(calculateData?.content?.value ?? 0);
			const preview = finishData;

			const previewsProcedureBody: PostPreviewsProcedureParams = {
				procedureCode,
				serviceDate: format(new Date(), 'dd/MM/yyyy'),
				procedureInformationValue: value,
				procedureAmount: value,
				protocolDetail: {
					protocol: String(preview?.protocol ?? preview?.protocolCode ?? ''),
					protocolCode: String(preview?.protocolCode ?? ''),
					request: String(preview?.request ?? ''),
					requestCode: String(preview?.requestCode ?? ''),
				},
				sequentialProcedure: '1',
				type: 'P',
				quantity: '1',
				statisticsGroup: '',
				startTime: '',
				endTime: '',
				procedurePrincipal: '',
				access: '',
				specialty: {
					code: '',
					specialPercentage: '',
				},
			};

			await previewsProcedureMutation.mutateAsync(previewsProcedureBody);
			stepCompletedRef.current = 3;
		}

		if (stepCompletedRef.current < 4) {
			const finishData = lastFinishResultRef.current?.content ?? finishPreviewMutation.data?.content;
			const protocol = String(
				finishData?.protocolCode ?? '',
			);
			if (!protocol) return;
			await patchFinishRequestPreviewMutation.mutateAsync(protocol);
			stepCompletedRef.current = 4;
		}

		if (stepCompletedRef.current < 5) {
			const finishData = lastFinishResultRef.current?.content ?? finishPreviewMutation.data?.content;
			const protocolCode = finishData?.protocolCode ?? 0;
			const requestCode = Number(finishData?.requestCode ?? 0);

			if (protocolCode && requestCode) {
				try {
					const analysisResult = await postPreviewsAnalysisMutation.mutateAsync({
						protocolCode,
						requestCode,
						userCode: calculateUserCode,
					});
					setPreviewsAnalysisResult(analysisResult);
				} catch {
					postPreviewsAnalysisMutation.reset();
					setIsValueZero(true);
					return;
				}
			}
			stepCompletedRef.current = 5;
			handleNextStep?.();
		}
	};

	function handleResetValueZero() {
		setIsValueZero(false);
	}

	function resetFlowFromStart() {
		stepCompletedRef.current = 0;
		lastParamsRef.current = null;
		lastCalculateResultRef.current = null;
		lastFinishResultRef.current = null;
		calculatePreviewMutation.reset();
		finishPreviewMutation.reset();
		previewsProcedureMutation.reset();
		patchFinishRequestPreviewMutation.reset();
		postPreviewsAnalysisMutation.reset();
	}

	function handleResetErrors() {
		calculatePreviewMutation.reset();
		finishPreviewMutation.reset();
		previewsProcedureMutation.reset();
		patchFinishRequestPreviewMutation.reset();
		postPreviewsAnalysisMutation.reset();
	}

	return {
		handleFinishPreview,
		handleResetErrors,
		handleResetValueZero,
		stepCompleted: stepCompletedRef.current,
		isLoading,
		hasError,
		isValueZero,
	};
};